import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { logger } from 'app/core/util/logger';
import { forkJoin, Observable, of } from 'rxjs';
import { appSettings } from 'app/appSettings';
import { AdminDefinitionRestService } from '../../../services/rest/admin/admin-definition.rest.service';
import { I18nRestService } from '../../../services/rest/i18n.rest.service';
import { util } from '../../../util/util';
import { RootState } from '../../index';
import * as actions from './i18n.actions';
import { LanguageLoadAction, LanguageLoadAllAction, LanguageSaveAction, LanguageSaveAllAction } from './i18n.actions';
import { catchError, filter, map, startWith, switchMap, withLatestFrom } from 'rxjs/operators';
import { AuthenticationService } from '../../../authentication/authentication.service';

@Injectable({ providedIn: 'root' })
export class I18nEffects {

  private authenticationService = inject(AuthenticationService);

  loadLanguageJson$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<LanguageLoadAction>(actions.LANGUAGE_LOAD),
      withLatestFrom(this.authenticationService.authenticated$, (action, isAuthenticated) => ({
        action,
        isAuthenticated,
      })),
      filter(({ isAuthenticated }: { isAuthenticated: boolean }) => isAuthenticated),
      switchMap(
        (
          { action }: { action: LanguageLoadAction; isAuthenticated: boolean } // Register to this event, unregister previous if new event occurs.
        ) =>
          this.i18nRestService.getServerTranslations(action.payload.language).pipe(
            map(translations => new actions.LanguageLoadSuccessAction(translations)),
            catchError(err => {
              return of(new actions.LanguageLoadFailAction(err));
            })
          )
      )
    )
  );

  saveLanguageJson$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<LanguageSaveAction>(actions.LANGUAGE_SAVE),
      withLatestFrom(this.authenticationService.authenticated$, this.store, (action, isAuthenticated, state) => ({
        isAuthenticated,
        language: action.payload.language,
        json: action.payload.json,
        multisave: state.i18n.multisave,
      })),
      filter(({ isAuthenticated }: { isAuthenticated: boolean }) => isAuthenticated),
      switchMap(
        (
          { language, json, multisave }: { language: string; json: string; multisave: boolean; isAuthenticated: boolean } // Register to this event, unregister previous if new event occurs.
        ) =>
          this.i18nRestService.saveOrUpdateTranslations(language, json).pipe(
            map(() => {
              if (multisave) {
                return new actions.LanguageSaveAllSuccessAction();
              } else {
                return new actions.LanguageSaveSuccessAction();
              }
            }),
            catchError(err => {
              return of(new actions.LanguageSaveFailAction(err));
            })
          )
      )
    )
  );

  setReferenceLanguageJsons$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        startWith(new actions.LanguageSetReferenceLanguageAction()),
        ofType(actions.LANGUAGE_SET_REFERENCE_LANGUAGE),
        switchMap(() => this.authenticationService.authenticated$),
        filter((isAuthenticated : boolean) => isAuthenticated),
        switchMap(() => {
          const checkedIn$ = this.i18nRestService.getCheckedInTranslations(appSettings.DEFAULT_LANGUAGE).subscribe(translations =>
            this.store.dispatch(new actions.LanguageSetReferenceLanguageSuccessAction({json: translations}))
          );
          return of(null);
        })
      ), { dispatch: false });

  /**
   *
   * This effect will save the csv input as jsons in the database.  Only languages in the header are saved.
   * Keys that are not known (not in CSV) are added and already known values from DB are used
   * So it's possible to uplaod a subset of translations (subset of keys or subset of languages)
   * @type {Observable<any>}
   */
  saveAllLanguagesJson$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType<LanguageSaveAllAction>(actions.LANGUAGE_SAVE_ALL),
        withLatestFrom(this.authenticationService.authenticated$, this.store, (action, isAuthenticated, state) => ({
          isAuthenticated,
          csv: action.payload,
          allJson: state.i18n.allJson,
        })),
        filter(({ isAuthenticated }: { isAuthenticated: boolean }) => isAuthenticated),
        switchMap(({ csv, allJson }: { csv: any; allJson: any; isAuthenticated: boolean }) => {
          // CSV to json for each language
          const rows = csv.split('\n');
          const header = rows.splice(0, 1)[0];
          const languages = header.split('\t');
          languages.splice(0, 1)[0];
          const translations = languages.reduce((result, language) => {
            return {
              ...result,
              [language]: {},
            };
          }, {});

          for (const row of rows) {
            const cols = row.split('\t');
            const key = cols.splice(0, 1)[0];
            if (key) {
              for (let i = 0; i < languages.length; i++) {
                const lang = languages[i];
                translations[lang][key] = cols[i];
              }
            }
          }

          // Add missing keys from original one (if loaded)
          languages.forEach(language => {
            allJson[language] &&
            Object.keys(allJson[language]).forEach(key => {
              if (!translations[language][key]) {
                translations[language][key] = allJson[language][key];
              }
            });
          });
          Object.keys(translations).forEach(language => {
            if (language !== 'default') {
              this.removeEmptyTranslations(language, translations[language], allJson['default']);
              translations[language] = util.unflattenJson(translations[language]);
              this.store.dispatch(new actions.LanguageSaveAction({ language, json: translations[language] }));
            }
          });
          logger.debug(translations);
          return of(null);
        })
      ),
    { dispatch: false }
  );

  saveAllLanguagesSuccessJson$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LANGUAGE_SAVE_ALL_SUCCESS),
      withLatestFrom(this.authenticationService.authenticated$, this.store, (action, isAuthenticated, state) => ({
        action,
        isAuthenticated,
        state,
      })),
      filter(({ isAuthenticated }: { isAuthenticated: boolean }) => isAuthenticated),
      switchMap(() => {
        return of(new LanguageLoadAllAction());
      })
    )
  );

  private checkedInLanguages: string[] = ['bg', 'cs', 'da', 'de', 'el', 'en', 'es', 'fr', 'hu', 'it', 'nl', 'pl', 'pt', 'ro', 'sk', 'tr', 'uk', 'hr', 'fi'];

  resetLanguageJsons$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.LANGUAGE_RESET_SERVER),
        withLatestFrom(this.authenticationService.authenticated$, (action, isAuthenticated) => ({
          action,
          isAuthenticated,
        })),
        filter(({ isAuthenticated }: { isAuthenticated: boolean }) => isAuthenticated),
        switchMap(() => {
          this.checkedInLanguages.forEach(lang => {
            this.i18nRestService.getCheckedInTranslations(lang).subscribe(translations => {
              if (translations) {
                this.i18nRestService.saveOrUpdateTranslations(lang, util.unflattenJson(translations)).subscribe(() => {
                });
              }
            });
          });
          // Get all languages, check if in DB

          return of(null);
        })
      ),
    { dispatch: false }
  );

  setLanguageJsons$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.LANGUAGE_SET_MISSING_SERVER),
        withLatestFrom(this.authenticationService.authenticated$, (action, isAuthenticated) => ({
          action,
          isAuthenticated,
        })),
        filter(({ isAuthenticated }: { isAuthenticated: boolean }) => isAuthenticated),
        switchMap(() => {
          this.checkedInLanguages.forEach(lang => {
            const checkedIn$ = this.i18nRestService.getCheckedInTranslations(lang);
            const server$ = this.i18nRestService.getServerTranslations(lang);
            forkJoin([checkedIn$, server$]).subscribe(results => {
              const checkedInVersion = results[0];
              const serverVersion = results[1];
              if (!serverVersion && checkedInVersion) {
                this.i18nRestService.saveOrUpdateTranslations(lang, util.unflattenJson(checkedInVersion)).subscribe(() => {
                });
              }
            });
          });
          // Get all languages, check if in DB

          return of(null);
        })
      ),
    { dispatch: false }
  );

  loadLanguagesJson$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LANGUAGE_LOAD_ALL),
      withLatestFrom(this.authenticationService.authenticated$, (action, isAuthenticated) => ({
        action,
        isAuthenticated,
      })),
      filter(({ isAuthenticated }: { isAuthenticated: boolean }) => isAuthenticated),
      switchMap(() =>
        // Register to this event, unregister previous if new event occurs.
        this.i18nRestService.getServerTranslations(this.checkedInLanguages).pipe(
          map(
            translations =>
              new actions.LanguageLoadAllSuccessAction({
                translations,
                languages: this.checkedInLanguages,
              })
          ),
          catchError(err => {
            return of(new actions.LanguageLoadAllFailAction(err));
          })
        )
      )
    )
  );

  constructor(
    private store: Store<RootState>,
    private actions$: Actions,
    private adminDefinitionRestService: AdminDefinitionRestService,
    private i18nRestService: I18nRestService,
  ) {
  }

  removeEmptyTranslations(language, translation, defaultTranslation) {
    const keys = Object.keys(translation);
    keys.forEach(key => {
      if ((!translation[key] || translation[key].trim() === '') && defaultTranslation[key] && defaultTranslation[key].trim() !== '') {
        delete translation[key];
      }
    });
    const newKeys = Object.keys(translation);
    logger.debug(() => `language ${language}, old: ${keys.length}, new: ${newKeys.length}`);
  }
}
